import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Loader } from '../../Common/Loader/loader';
import { IoIosArrowUp } from 'react-icons/io';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoIosArrowDown } from 'react-icons/io';
import { useTheme } from '@mui/material/styles';
import { httpsCallable } from '@firebase/functions';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { useDispatch, useSelector } from 'react-redux';
import { ShowErrorToast } from '../../Common/Toast/toast';
import { functions } from '../../../Infrastructure/config';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { StepsHeader } from '../../Common/StepsHeader/stepsHeader';
import { StepsFooter } from '../../Common/StepsFooter/stepsFooter';
import { classScheduleSx, ClassScheduleTitle, ErrorBlockSmall } from '../styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { setCourseDetails } from '../../../Infrastructure/States/courseDetailsSlice';
import { decrementCoursesSteps, incrementCoursesSteps } from '../../../Infrastructure/States/coursesStepsSlice';
import { calculateFirstClassDates, calculateLastClassDates } from '../../../Infrastructure/Utilities/CalculateClassDates';
import { Box, Checkbox, InputAdornment, Typography, useMediaQuery } from '@mui/material';
import { TimePickerDropdown } from '../../Common/TimePickerDropdown/timePickerDropdown';
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import useAuthentication from '../../../Infrastructure/States/onAuthStateChange';
import { convertTimestampToDay } from '../../../Infrastructure/Utilities/convertTimestampToDay';
import { updateTimesKeysWithStartTime } from '../../../Infrastructure/Utilities/classScheduleFormat';
import { generateWeekDays } from '../../../Infrastructure/Utilities/generateWeekDays';
import { generateWeekDaysFromSavedTimes } from '../../../Infrastructure/Utilities/generateWeekDaysFromSavedTimes';
import { capitalizeFirstLetter } from '../../../Infrastructure/Utilities/capitalizeFirstLetter';
import paths from '../../../Infrastructure/Router/paths';
import { USER_LOGS_CLIENT } from '../../../Infrastructure/Constants/logsConstant';
import { safeClientLogEvent } from '../../../Infrastructure/Utilities/safeClientLogEvent';
import { StyledOutlinedTextField } from '../../../Infrastructure/Theme/styledComponents';
dayjs.extend(utc);
dayjs.extend(timezone);

export const ClassSchedule = () => {
    const theme = useTheme();
    const errorRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation()
    const { userId } = useAuthentication()
    const [error, setError] = useState(null);
    const [submitCount, setSubmitCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const ip = useSelector((state) => state.ip.address)
    const page = useSelector((state)=> state.route.currentRoute)
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const userData = useSelector((state) => state.userData.data)
    const userTimezone = userData?.account?.timezone
    const course = useSelector((state) => state.course.courseDetails)
    const courseId = course?.courseId || null
    const classSchedule = course?.course?.courseDetails?.class_schedule;
    const firstClassScheduleKey = classSchedule && Object.keys(classSchedule)[0];
    const firstClassSchedule = classSchedule && classSchedule[firstClassScheduleKey];
    const first_class = firstClassSchedule?.first_class;
    const course_times = firstClassSchedule?.times;
    const [firstClass, setFirstClass] = useState(first_class ? dayjs.tz(first_class, userTimezone) : null)
    const courseSteps = useSelector((state) => state.courseSteps.courseSteps);
    const loading = useSelector((state) => state.userData.loading);
    const course_duration = firstClassSchedule?.duration;
    const [duration, setDuration] = useState(course_duration || "");
    const [maxLearners, setMaxLearners] = useState(firstClassSchedule?.max_learners || '50');
    const [formData, setFormData] = useState({})
    const [hasUserEditedTimes, setHasUserEditedTimes] = useState(false);
    
    const handleExit = async () => {
        try {
            setIsLoading(true)
            await saveTimes(5)
            navigate(paths.dashboardPage.path);
        } catch (error) {
            navigate(paths.dashboardPage.path);
            ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TEXT)
        } finally {
            setIsLoading(false)
        }
    };
    const handleDec = async () => {
        if (courseSteps > 1) {
            try {
                setError('')
                setIsLoading(true)
                await saveTimes(4);
                const course = userData?.educator?.courses[courseId ? courseId : ''];
                if (course) {
                    dispatch(setCourseDetails({ course, courseId }));
                }
                dispatch(decrementCoursesSteps());
            } catch (error) {
                ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST);
                await safeClientLogEvent({
                    ...USER_LOGS_CLIENT.CLASS_SCHEDULE_STEP_DECREMENT_FAILED,
                    uid: userId || null,
                    source: "classScheduleStep.jsx",
                    emailInfo: null,
                    metadata: null,
                    page: page || null,
                    error: error?.message || null
                });
            } finally {
                setIsLoading(false)
            }
        }
    };
    const handleInc = async () => {
        if (courseSteps > 1) {
            setSubmitCount(prev => prev + 1);
            setError(null)
            if (firstClass === null || firstClass === '') {
                setError('First class date is required');
                return;
            }
            if (
                duration === null || duration === '' ||
                parseInt(duration, 10) === 0 ||
                parseInt(duration, 10) > 26
            ) {
                setError('Duration must be between 1 and 26 weeks.');
                return;
            }
            if (
                maxLearners === null || maxLearners === '' ||
                parseInt(maxLearners, 10) < 5 ||
                parseInt(maxLearners, 10) > 50
            ) {
                setError('Learners must be between 5 and 50.');
                return;
            }
            const isAtLeastOneDaySelected = Object.values(formData).some(
                (day) => day.checked
            );
            if (!isAtLeastOneDaySelected) {
                setError('Please select at least one day.');
                return;
            }
            const firstClassDay = firstClass.format('dddd').toLowerCase();
            const selectedDayKey = Object.keys(formData).find(
                (day) => formData[day].checked
            );
            const selectedDay = selectedDayKey ? dayjs(Number(selectedDayKey)).tz(userTimezone).format('dddd').toLowerCase() : null;
            if (selectedDay && firstClassDay !== selectedDay) {
                setError('First class date must match earliest selected day in the week');
                return;
            }
            for (const day in formData) {
                const dayString = convertTimestampToDay(day, userTimezone);
                if (
                    formData[day].checked &&
                    (!formData[day].start || !formData[day].end)
                ) {
                    setError(
                        `Fill both start and end times for ${capitalizeFirstLetter(dayString)}`
                    );
                    return;
                }
                const start = dayjs(formData[day].start);
                const end = dayjs(formData[day].end);
                if (formData[day].checked && end.isBefore(start)) {
                    setError(
                        `End time must be after start time for ${capitalizeFirstLetter(dayString)}`
                    );
                    return;
                }
                // Check if the gap between start and end times is greater than 30 minutes
                const timeGapHours = end.diff(start, 'hours');
                if (formData[day].checked && timeGapHours > 3) {
                    setError(`Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be greater than 3 hours.`);
                    return;
                }
                // Check if the gap between start and end times is greater than 30 minutes
                const timeGapMinutes = end.diff(start, 'minutes');
                if (formData[day].checked && timeGapMinutes < 30) {
                    setError(`Gap between start and end times for ${capitalizeFirstLetter(dayString)} should not be less than 30 minutes.`);
                    return;
                }

            }
            const times = {};
            for (const day in formData) {
                if (formData[day].checked) {
                    const unixStartTimeStamp = Math.floor(new Date(formData[day]?.start).getTime());
                    const unixEndTimeStamp = Math.floor(new Date(formData[day]?.end).getTime());
                    times[day] = {
                        start: unixStartTimeStamp,
                        end: unixEndTimeStamp
                    };
                }
            }
            const old_times = times ? times : course_times
            const sortedTimesObject =
                old_times &&
                Object.fromEntries(
                    Object.entries(old_times).sort(([dayA], [dayB]) => {
                        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                        return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
                    })
                );
            const days = sortedTimesObject && Object.keys(sortedTimesObject);
            const propFirstClass = {
                day: days[0],
                timing: sortedTimesObject[days[0]]
            }
            const propLastClass = {
                day: days[days.length - 1],
                timing: sortedTimesObject[days[days?.length - 1]]
            }
            setError('');
            setIsLoading(true)
            try {
                const formattedTimes = updateTimesKeysWithStartTime(times)
                const firstClassDate = calculateFirstClassDates(propFirstClass, firstClass, userTimezone)
                const lastClassDate = calculateLastClassDates(propLastClass, firstClass, duration, userTimezone, formattedTimes)
                const updateClassScheduleStep = httpsCallable(
                    functions,
                    'updateclassschedulestep'
                );
                await updateClassScheduleStep({ firstClassString: firstClassDate, lastClassString: lastClassDate, duration, max_learners: maxLearners, times: formattedTimes, courseId, classId: firstClassScheduleKey, ip, page });
                const course = userData?.educator?.courses[courseId ? courseId : ''];
                if (course) {
                    dispatch(setCourseDetails({ course, courseId }))
                }
                dispatch(incrementCoursesSteps())
                setError('');
            } catch (error) {
                setError(ERROR_CONSTANTS.GENERIC_ERROR_TEXT)
            } finally {
                setIsLoading(false)
            }
        }
    };
    const handleCheckboxChange = (dayKey) => {
        setHasUserEditedTimes(true);
        setFormData((prevFormData) => {
            const updatedDay = { ...prevFormData[dayKey] }
            const isChecked = !updatedDay.checked
            updatedDay.checked = isChecked
            return {
                ...prevFormData,
                [dayKey]: updatedDay,
            }
        })
    }
    const handleTimeChange = (dayKey, field, timeValue) => {
        if (!timeValue) return
        setHasUserEditedTimes(true);
        setFormData((prevFormData) => {
            const baseDay = dayjs(parseInt(dayKey)).tz(userTimezone)
            const updatedDateTime = baseDay
                .hour(timeValue.hour())
                .minute(timeValue.minute())
                .second(0)
                .millisecond(0)
                .valueOf()
            const updatedDay = { ...prevFormData[dayKey] };
            updatedDay[field] = updatedDateTime;
            if ((updatedDay.start || updatedDay.end) && !updatedDay.checked) {
                updatedDay.checked = true;
            }
            return {
                ...prevFormData,
                [dayKey]: updatedDay
            }
        })
    };
    const handleDurationChange = (e) => {
        const newValue = e.target.value.replace(/[^0-9]/g, '');
        const newDuration = newValue ? Math.min(parseInt(newValue, 10), 26) : '';
        setDuration(newDuration);
    };
    const incrementDuration = () => {
        setDuration((prevDuration) => Math.min(prevDuration + 1, 26));
    };
    const decrementDuration = () => {
        setDuration((prevDuration) => Math.max(prevDuration - 1, 1));
    };
    const handleMaxLearnersChange = (e) => {
        const newValue = e.target.value.replace(/[^0-9]/g, '');
        const newMaxLearners = newValue ? Math.min(parseInt(newValue, 10), 50) : '';
        setMaxLearners(newMaxLearners)
    };
    const incrementMaxLearners = () => {
        setMaxLearners((prevRange) => Math.min(prevRange + 1, 50));
    };
    const decrementMaxLearners = () => {
        setMaxLearners((prevRange) => Math.max(prevRange - 1, 5));
    };
    useEffect(() => {
        if (hasUserEditedTimes) return;
        let updatedWeekDays;
        if (!course_times) {
            updatedWeekDays = generateWeekDays(formData, userTimezone);
        } else {
            updatedWeekDays = generateWeekDaysFromSavedTimes(formData, userTimezone, course_times);
        }
        setFormData((prev) => {
            const prevString = JSON.stringify(prev);
            const nextString = JSON.stringify(updatedWeekDays);
            return prevString !== nextString ? updatedWeekDays : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_times, firstClass, formData, userTimezone]);

    const saveTimes = async (step) => {
        let times = {};
        // wait until form Data is processed
        let attempts = 0;
        while (attempts < 5) {
            times = {};
            for (const day in formData) {
                if (formData[day].checked) {
                    const unixStartTimeStamp = Math.floor(new Date(formData[day]?.start).getTime());
                    const unixEndTimeStamp = Math.floor(new Date(formData[day]?.end).getTime());
                    times[day] = {
                        start: unixStartTimeStamp,
                        end: unixEndTimeStamp
                    };
                }
            }
            if (Object.keys(times).length > 0) break
            await new Promise(resolve => setTimeout(resolve, 300))
            attempts++
        }
        const old_times = times ? times : course_times
        const sortedTimesObject =
            old_times &&
            Object.fromEntries(
                Object.entries(old_times).sort(([dayA], [dayB]) => {
                    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
                })
            );
        const days = sortedTimesObject && Object.keys(sortedTimesObject);
        const propFirstClass = {
            day: days[0],
            timing: sortedTimesObject[days[0]]
        }
        const firstClassDate = firstClass
        ? Object.keys(times).length > 0
            ? calculateFirstClassDates(propFirstClass, firstClass, userTimezone)
            : dayjs(firstClass).valueOf()
        : null;
        const formattedTimes = updateTimesKeysWithStartTime(times)
        const updateClassScheduleStep = httpsCallable(
            functions,
            'updateclassschedulestep'
        );
        await updateClassScheduleStep({ step, firstClassString: firstClassDate, duration, max_learners: maxLearners, times: formattedTimes, courseId, classId: firstClassScheduleKey, ip, page });
        setError('');
    };
    useEffect(() => {
        if (first_class && userTimezone) {
            const userTzFirstClass = dayjs(Number(first_class)).tz(userTimezone).startOf('day');
            setFirstClass(userTzFirstClass);
        }
        // Check if course_times exists and is an object
        if (course_times && typeof course_times === 'object') {
            const updatedFormData = { ...formData };
            for (const day in formData) {
                if (course_times[day]) {
                    updatedFormData[day] = {
                        start: dayjs(course_times[day].start).format('YYYY-MM-DDTHH:mm'),
                        end: dayjs(course_times[day].end).format('YYYY-MM-DDTHH:mm'),
                        checked: true,
                    };
                }
            }
            setFormData(updatedFormData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTimezone, first_class]);

    const handleFirstClassData = (date) => setFirstClass(date ? dayjs.tz(date, userTimezone).startOf('day') : null)
    useEffect(() => {
        if (first_class && userTimezone) {
            const converted = dayjs.utc(Number(first_class)).tz(userTimezone);
            setFirstClass(converted);
        }
    }, [first_class, userTimezone]);
    useEffect(() => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
    }, [pathname])
    useEffect(() => {
        if (!isXs) return
        if (!error) return
        requestAnimationFrame(() => {
            if (errorRef.current) {
                errorRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        });
    }, [submitCount, error, isXs])
    return (
        <Box sx={classScheduleSx.root}>
            {/* Header Box */}
            <Box sx={classScheduleSx.headerBox}>
                <StepsHeader steps={courseSteps} handleExit={handleExit} />
            </Box>
            <Box 
                component={'form'}
                sx={classScheduleSx.form}
            >
                {loading || isLoading ? (
                    <Loader />
                ) : (
                    <Box sx={classScheduleSx.content}>
                        <Box sx={classScheduleSx.pageTextDesc}>
                            <ClassScheduleTitle>
                                Pick the class schedule for your first cohort. You will be able
                                to add more cohorts once the course is accepted, You can pick
                                any times and days that work for you. For most courses, we
                                recommend 1-2 hours per day, 2-3 days per week, and a duration
                                of 8-16 weeks. However, choose the schedule that works for you
                                and fits your course the best.
                            </ClassScheduleTitle>
                        </Box>
                        <Grid container sx={classScheduleSx.mainGridBox} >
                            <Grid lg={4} md={10} sm={12} xs={12} sx={classScheduleSx.subGrid1}>
                                <Box sx={classScheduleSx.subGrid1Box}>
                                    <Box sx={classScheduleSx.firstClassBox}>
                                        <Typography variant='body1' color='initial' sx={classScheduleSx.firstClassTitle}>
                                            Date of first class
                                        </Typography>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Date of first class"
                                                value={firstClass}
                                                onChange={handleFirstClassData}
                                                disablePast
                                                format="dddd, MMM D, YYYY"
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: 'outlined',
                                                    },
                                                }}
                                                shouldDisableDate={(date) => {
                                                    const today = dayjs().startOf('day');
                                                    const oneWeekFromToday = today.add(7, 'day');
                                                    return date.isBefore(oneWeekFromToday);
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                    <Box sx={classScheduleSx.courseDurationBox}>
                                        <Typography variant='body1' color='initial' sx={classScheduleSx.courseDurationTitle}>
                                            Course Duration
                                        </Typography>
                                        <StyledOutlinedTextField
                                            id='outlined-number'
                                            label='Weeks'
                                            type='number'
                                            fullWidth
                                            variant='outlined'
                                            onChange={handleDurationChange}
                                            value={duration}
                                            InputProps={{
                                                onKeyPress: (event) => {
                                                    if (
                                                        isNaN(event.key) ||
                                                        (event.key === '-' && event.code === 'ArrowDown')
                                                    ) {
                                                        event.preventDefault();
                                                    }
                                                },
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Box sx={classScheduleSx.courseDurationAdornment}>
                                                            <IoIosArrowUp
                                                                onClick={incrementDuration}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    ':hover': { color: 'secondary' },
                                                                }}
                                                            />
                                                            <IoIosArrowDown
                                                                onClick={decrementDuration}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    hover: { color: 'primary' },
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={classScheduleSx.maxLearnersBox}>
                                        <Typography variant='body1' color='initial' pb={2}>
                                            Max Learners
                                        </Typography>
                                        <StyledOutlinedTextField
                                            id='maxLearners'
                                            label='Max Learners'
                                            type='number'
                                            fullWidth
                                            variant='outlined'
                                            onChange={handleMaxLearnersChange}
                                            value={maxLearners}
                                            InputProps={{
                                                onKeyPress: (event) => {
                                                    if (
                                                        isNaN(event.key) ||
                                                        (event.key === '-' && event.code === 'ArrowDown')
                                                    ) {
                                                        event.preventDefault();
                                                    }
                                                },
                                                endAdornment: (
                                                    <InputAdornment position='end'>
                                                        <Box
                                                            sx={classScheduleSx.maxLearnersAdornment}
                                                        >
                                                            <IoIosArrowUp
                                                                onClick={incrementMaxLearners}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    ':hover': { color: 'secondary' },
                                                                }}
                                                            />
                                                            <IoIosArrowDown
                                                                onClick={decrementMaxLearners}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    hover: { color: 'primary' },
                                                                }}
                                                            />
                                                        </Box>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid lg={8} md={10} sm={12} xs={12}>
                                {/* add a text field of tz small black text, bolded  */}
                                <Typography variant='body1' color='initial' sx={classScheduleSx.timezoneText}>
                                    Times are in {dayjs().tz(userTimezone).format('z')}
                                </Typography>
                                <Box>
                                    {formData && Object.keys(formData).map((day) => (
                                        <Box
                                            key={day}
                                            sx={classScheduleSx.dayBox}
                                        >
                                            <Checkbox
                                                checked={formData[day]?.checked}
                                                onChange={() => handleCheckboxChange(day)}
                                                sx={classScheduleSx.dayCheckBox}
                                            />
                                            <Box sx={classScheduleSx.dayTimeBox}>
                                                <Box sx={classScheduleSx.dayNameTextBox}>
                                                    <Typography
                                                        variant='body1'
                                                        color='initial'
                                                        sx={classScheduleSx.dayNameTextField}
                                                    >
                                                        {day ? convertTimestampToDay(day, userTimezone) : "loading"}
                                                        <Typography
                                                            component="span"
                                                            sx={classScheduleSx.dayNameColonField}
                                                        >
                                                            :
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                                <Box sx={classScheduleSx.selectTimeBox}>
                                                    {/* Start Time Dropdown */}
                                                    <TimePickerDropdown
                                                        label="Start Time"
                                                        value={formData[day]?.start ? dayjs(formData[day].start).tz(userTimezone) : null}
                                                        onChange={(value) => handleTimeChange(day, 'start', value)}
                                                        width={230}
                                                    />
                                                    {/* End Time Dropdown */}
                                                    <TimePickerDropdown
                                                        label="End Time"
                                                        value={formData[day]?.end ? dayjs(formData[day].end).tz(userTimezone) : null}
                                                        onChange={(value) => handleTimeChange(day, 'end', value)}
                                                        width={230}
                                                    />

                                                </Box>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                                <ErrorBlockSmall sx={classScheduleSx.error} ref={errorRef}>
                                    {error}
                                </ErrorBlockSmall>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>
            <Box sx={classScheduleSx.footerBox}>
                <StepsFooter
                    handleContinueClick={handleInc}
                    error={error}
                    selectedDates={formData}
                    handleDec={handleDec}
                    isLoading={isLoading}
                />
            </Box>
        </Box>
    );
};